<script setup lang="ts">
import Card from '@/components/common/Card.vue';
import HintPopover from '@/components/common/HintPopover.vue';
import PrimaryButton from '@/components/common/PrimaryButton.vue';
import Badge from '@/components/common/Badge.vue';
import TextInput from '@/components/form/TextInput.vue';
import { formatDate, useDebounceFn } from '@vueuse/core';
import * as yup from 'yup';
import CardSkeleton from '@/components/skeletons/CardSkeleton.vue';
import DomainSelectPlanModal from '@/modules/cleepo/components/domain/DomainSelectPlanModal.vue';
import DomainRegisteredModal from '@/modules/cleepo/components/domain/DomainRegisteredModal.vue';

const { t } = useTranslations();
const { landing } = useProjectLanding();
const { isPaid } = useCurrentProject();

const query = ref('');
const domain = ref('');
const handleDomainLookup = () => {
  domain.value = query.value;
};

const handleDomainLookupDebounced = useDebounceFn(handleDomainLookup, 500);

const {
  domain: domainLookup,
  isLoading: isDomainLookupLoading,
  registerDomain,
} = useDomainLookup(domain);

const { domains: accountDomains, isLoading: isAccountDomainsLoading } = useAccountDomains();

const hasAccountDomains = computed(() => (accountDomains.value?.length ?? 0) > 0);

const { publishers, isLoading: isPublishersLoading } = useLandingPublishers(
  () => landing.value?.id,
);

const accountDomainsPublishers = computed(() => {
  return accountDomains.value?.map((domain) => {
    return {
      ...domain,
      publisher: publishers.value?.find((publisher) =>
        publisher.publication_url.includes(domain.name),
      ),
    };
  });
});

const modal = useModal();
const isSelectPlanModalOpen = ref(false);
const isRegisteringDomain = ref(false);
const isRegisteredDomainModalOpen = ref(false);

function handleDomainRegister() {
  // If not paid, show plan subscription modal
  if (!isPaid.value) {
    isSelectPlanModalOpen.value = true;
    return;
  }

  try {
    modal.confirm({
      title: t('cleepo.domain.register_domain_modal.title'),
      description: t('cleepo.domain.register_domain_modal.description'),
      confirmButtonText: t('cleepo.domain.register_domain_modal.confirm'),
      cancelButtonText: t('cleepo.domain.register_domain_modal.cancel'),
      onConfirm: async () => {
        isRegisteringDomain.value = true;
        await registerDomain(domain.value);
        isRegisteringDomain.value = false;
        isRegisteredDomainModalOpen.value = true;
      },
    });
  } catch (error) {
    console.error(error);
  }
}
</script>

<template>
  <div>
    <div class="flex items-center gap-2">
      <h1 class="text-2xl font-medium">{{ t('cleepo.domain.title') }}</h1>
      <HintPopover :title="t('cleepo.domain.title')">
        <template #default>
          <p class="max-w-4xl text-sm text-semantic-text-secondary">
            {{ t('cleepo.domain.description') }}
          </p>
        </template>
      </HintPopover>
    </div>
    <div class="mt-4" v-if="isPublishersLoading || isAccountDomainsLoading">
      <CardSkeleton class="!h-32 w-full" />
    </div>
    <div v-else-if="hasAccountDomains" class="mt-4 space-y-4">
      <Card v-for="domain in accountDomainsPublishers" :key="domain.id" class="bg-white">
        <div class="flex items-center justify-between gap-4 border-b py-3 last:border-b-0">
          <div class="min-w-0">
            <div class="flex items-center gap-2 text-brand-primary">
              <IconifyIcon icon="heroicons:globe-alt" class="size-5" />
              <p class="truncate">{{ domain.name }}</p>
            </div>
            <div
              class="mt-2 flex flex-wrap items-center gap-2 text-sm text-semantic-text-secondary"
            >
              <Badge color="gray" size="sm" class="flex items-center gap-1">
                <IconifyIcon icon="heroicons:building-office-2" class="size-4" />
                {{ domain.registrar_provider }}
              </Badge>
              <span>•</span>
              <span
                >{{ t('cleepo.domain.registered') }}:
                {{ formatDate(new Date(domain.registration_date), 'DD/MM/YYYY') }}</span
              >
              <span>•</span>
              <span
                >{{ t('cleepo.domain.expires') }}:
                {{ formatDate(new Date(domain.expiration_date), 'DD/MM/YYYY') }}</span
              >
            </div>
          </div>
        </div>
      </Card>
    </div>
    <!-- No domains, can register one -->
    <div v-else class="mt-4 max-w-xl">
      <form @submit.prevent="handleDomainLookupDebounced">
        <TextInput
          v-model="query"
          :label="t('cleepo.domain.lookup_domain')"
          name="domain_lookup"
          hint="El dominio puede ser .com o .net"
          :validations="
            yup
              .string()
              .required(t('fields.validations.required'))
              .matches(
                /^(?!:\/\/)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
                t('fields.validations.domain'),
              )
              .max(253, t('fields.validations.max', { max: 253 }))
          "
          :placeholder="t('cleepo.domain.lookup_domain_placeholder')"
        >
          <template #leadingIcon>
            <IconifyIcon icon="heroicons:globe-alt" />
          </template>
          <template #trailing>
            <PrimaryButton
              type="submit"
              class="rounded-l-none"
              :is-loading="isDomainLookupLoading"
              @click="handleDomainLookup"
            >
              {{ t('cleepo.domain.lookup_domain_button') }}
            </PrimaryButton>
          </template>
        </TextInput>
      </form>

      <Card v-if="domainLookup" class="mt-4 bg-white">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h3 class="text-lg font-medium">{{ domainLookup.domain }}</h3>
            <div class="mt-1 flex items-center gap-2">
              <Badge :color="domainLookup.available ? 'green' : 'red'">
                <IconifyIcon
                  :icon="domainLookup.available ? 'heroicons:check-circle' : 'heroicons:x-circle'"
                  class="mr-1 size-4"
                />
                {{
                  domainLookup.available
                    ? t('cleepo.domain.available')
                    : t('cleepo.domain.unavailable')
                }}
              </Badge>
            </div>
          </div>
          <div class="shrink-0">
            <PrimaryButton
              size="sm"
              :is-loading="isRegisteringDomain"
              :disabled="!domainLookup.available"
              @click="handleDomainRegister"
              >{{ t('cleepo.domain.register_domain_button') }}</PrimaryButton
            >
          </div>
        </div>
      </Card>

      <DomainSelectPlanModal v-model="isSelectPlanModalOpen" />
    </div>

    <DomainRegisteredModal v-model="isRegisteredDomainModalOpen" />
  </div>
</template>

<style scoped lang="scss"></style>
